/* eslint-disable jsx-a11y/click-events-have-key-events */
"use client";

import { getPlural, safeParseNumber } from "@alba-cars/common-modules";
import { useCallback, useEffect, useState } from "react";

import { useVehicleFiltersData } from "../_hooks/useVehicleFilterData";
import { useFilterContext } from "./context/FilterContext";
import { useSharedCarsQuery } from "./context/QueryContext";
import MobileFilterSidebar from "./MobileFilterSidebar";
import SortDropdown from "./SortDropdown";
import SuggestionsGrid from "./suggested/SuggestionsGrid";

export default function BuyCarsHeader() {
  const { resetQuery, queryOptions, getCurrentFilters } = useFilterContext();
  const { setMakes, makesLookup, bodyTypeLookup } = useVehicleFiltersData();
  const [isClient, setIsClient] = useState(false);

  const filters = getCurrentFilters();

  const handleReset = useCallback(() => {
    setMakes(undefined);
    resetQuery();
  }, [resetQuery, setMakes]);

  const { data, isLoading } = useSharedCarsQuery();
  const { isStateUpdating } = useFilterContext();

  // Handle client-side hydration
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Format array of filter values with appropriate text
  const formatFilterArray = (filterValues: string[] | string | undefined, lookupMap: Map<string, any>) => {
    if (!filterValues) return "";

    if (!Array.isArray(filterValues)) {
      if (lookupMap) {
        return lookupMap.get(filterValues)?.name || filterValues;
      }
      return filterValues;
    }

    if (filterValues.length === 0) {
      return "";
    }

    if (filterValues.length === 1) {
      if (lookupMap) {
        return lookupMap.get(filterValues[0])?.name || filterValues[0];
      }
      return filterValues[0];
    }

    // Handle multiple values (up to 4)
    if (filterValues.length <= 3) {
      const mappedValues = filterValues.map((value) => (lookupMap ? lookupMap.get(value)?.name || value : value));

      const lastValue = mappedValues.pop();
      return `${mappedValues.join(", ")} and ${lastValue}`;
    } else {
      // More than 4 values
      const mappedValues = filterValues
        .slice(0, 2)
        .map((value) => (lookupMap ? lookupMap.get(value)?.name || value : value));
      return `${mappedValues.join(", ")} and ${filterValues.length - 2} more`;
    }
  };

  // Only calculate these values client-side to avoid hydration mismatches
  const isDataPending = isClient ? isLoading || isStateUpdating : true;
  const totalCars = isClient && data?.pages?.[0]?.data?.total ? safeParseNumber(data.pages[0].data.total) : 0;

  // Create dynamic header text based on filters - only used client-side
  const getHeaderText = () => {
    if (!isClient || totalCars === 0) return "";

    // Default text with count when no make or bodyType filters are applied
    if (!filters.make && !filters.bodyType) {
      return `Browse all ${getPlural(totalCars, "used car")} in Dubai`;
    }

    let text = "Browse all";

    // Handle make filter
    if (filters.make) {
      text += ` ${formatFilterArray(filters.make, makesLookup)}`;
    }

    // Handle bodyType filter
    // if (filters.bodyType) {
    //   text += ` ${formatFilterArray(filters.bodyType, bodyTypeLookup)}`;
    // }

    text += " cars in Dubai";

    return text;
  };

  // During SSR or initial render, show the skeleton loader
  if (!isClient || isDataPending) {
    return <SkeletonLoader />;
  }

  return (
    <>
      <div className="w-full lg:flex lg:items-center lg:justify-between lg:mb-6">
        <div className="w-full self-start lg:self-center">
          <h1 className="urbanist text-wrap pt-6 text-3xl font-semibold xs:text-nowrap lg:text-wrap lg:pb-0">
            {totalCars === 0 ? (
              <div className="inter flex flex-col items-start justify-between rounded-2xl bg-white px-5 py-4 text-lg font-normal text-secondary-foreground sxs:items-center md:flex-row">
                No vehicles match your criteria{" "}
                <span role="button" onClick={handleReset} className="text-lg text-primary" tabIndex={0}>
                  Reset all filters.
                </span>
              </div>
            ) : (
              <span className="max-w-[32ch] md:max-w-2xl">{getHeaderText()}</span>
            )}
          </h1>
        </div>

        {totalCars > 0 && (
          <div className="hidden lg:flex lg:items-center">
            <SortDropdown />
          </div>
        )}
      </div>

      {totalCars > 0 && (
        <div className="sticky top-0 z-50 bg-secondary py-2 transition duration-300 ease-in-out lg:hidden">
          <div className="mt-2 flex flex-col justify-between md:mb-4">
            <div className="flex gap-2 py-2 md:pb-0">
              <div className="flex w-2/12 sm:w-1/2">
                <MobileFilterSidebar />
              </div>
              <div className="flex w-10/12 sm:w-1/2">
                <SortDropdown />
              </div>
            </div>
          </div>
        </div>
      )}

      {totalCars === 0 ? (
        <div className="mt-8">
          <h2 className="urbanist mb-4 text-wrap text-3xl font-semibold xs:text-nowrap lg:text-wrap lg:pb-0">
            You might be interested in
          </h2>
          <SuggestionsGrid queryOptions={queryOptions} />
        </div>
      ) : null}
    </>
  );
}

const SkeletonLoader = () => {
  return (
    <>
      <div className="flex w-full items-center justify-between gap-2 md:mb-4 lg:mb-0">
        <div className="mb-4 h-full !w-60 space-y-1 self-start md:mb-0 md:!w-96">
          <div className="mt-2 h-[2.2rem] !w-full animate-pulse rounded-md bg-gray-200" />
          <div className="h-[2.2rem] !w-3/4 animate-pulse rounded-md bg-gray-200 md:!hidden" />
        </div>
        <div className="hidden h-14 !w-44 animate-pulse rounded-2xl bg-gray-200 md:!w-60 lg:block" />
      </div>
      <div className="flex justify-between gap-1 pb-2 pe-2 md:mb-0 lg:hidden">
        <div className="!h-12 w-2/12 animate-pulse rounded-md bg-gray-200 md:w-1/2" />
        <div className="ms-2 !h-12 w-10/12 animate-pulse rounded-md bg-gray-200 md:w-1/2" />
      </div>
    </>
  );
};
